import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  canonical, 
  ogImage,
  ogType = 'website',
  schemaData,
  noIndex = false,
  publishedTime,
  modifiedTime 
}) => {
  // Get the current site URL - ensure Facebook can access it
  const getCurrentSiteUrl = () => {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    // For SSR/build time, use production URL
    return 'https://umoonaopalmine.com.au';
  };
  
  const siteUrl = getCurrentSiteUrl();
  const siteName = 'Umoona Opal Mine & Museum';
  
  // Default high-quality og:image - using the new downloaded image
  const defaultImage = '/og-default.jpg'; // New high-quality OpenGraph image
  
  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;
  
  // Ensure absolute URL for og:image - this is critical for Facebook sharing
  const getAbsoluteImageUrl = (imagePath) => {
    if (!imagePath) return `${siteUrl}${defaultImage}`;
    
    // If already absolute URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // If relative path, make it absolute
    return `${siteUrl}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`;
  };
  
  const fullOgImage = getAbsoluteImageUrl(ogImage || defaultImage);

  // Ensure description is within optimal length (150-160 chars)
  const optimizedDescription = description && description.length > 160 
    ? description.substring(0, 157) + '...' 
    : description;

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={optimizedDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonical} />
      
      {/* Viewport and mobile optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Open Graph tags - Critical for Facebook sharing */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={optimizedDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:secure_url" content={fullOgImage.replace('http://', 'https://')} />
      <meta property="og:image:url" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:alt" content={`${siteName} - Underground Opal Mine Tours in Coober Pedy`} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_AU" />
      <meta property="og:determiner" content="the" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      
      {/* Additional meta for social sharing */}
      <meta name="theme-color" content="#f59e0b" />
      <link rel="image_src" href={fullOgImage} />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={optimizedDescription} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:site" content="@UmoonaOpalMine" />
      
      {/* Geographic and business info */}
      <meta name="geo.region" content="AU-SA" />
      <meta name="geo.placename" content="Coober Pedy" />
      <meta name="geo.position" content="-29.0135;134.7530" />
      <meta name="ICBM" content="-29.0135, 134.7530" />
      
      {/* Business/contact info */}
      <meta name="contact" content="admin@umoonaopalmine.com.au" />
      <meta name="copyright" content="Umoona Opal Mine & Museum" />
      
      {/* Search engine directives */}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="googlebot" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="bingbot" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Additional SEO tags */}
      <meta name="author" content="Umoona Opal Mine & Museum" />
      <meta name="language" content="en-AU" />
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      
      {/* Performance hints */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />

      {/* Note: hreflang tags removed - not needed for single-language (en-AU) site */}
      {/* hreflang is only needed for multilingual sites with alternate language versions */}

      {/* Schema.org structured data */}
      {schemaData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;